import { User, UserManager, WebStorageStateStore } from "oidc-client-ts";
import { resolveInitialLanguage } from "./i18n";

const authority =
  import.meta.env.VITE_OIDC_AUTHORITY ??
  `${window.location.origin}/auth/realms/obviowms`;

export const userManager = new UserManager({
  authority,
  client_id: "obviowms-web",
  redirect_uri: `${window.location.origin}/oidc/callback`,
  post_logout_redirect_uri: `${window.location.origin}/`,
  response_type: "code",
  scope: "openid profile email",
  automaticSilentRenew: false,
  userStore: new WebStorageStateStore({ store: window.sessionStorage })
});

export async function getUser(): Promise<User | null> {
  return userManager.getUser();
}

export async function login(forceLogin = false): Promise<void> {
  await userManager.signinRedirect({
    ...(forceLogin ? { prompt: "login" } : {}),
    extraQueryParams: { ui_locales: resolveInitialLanguage() },
    state: { returnUrl: "/" }
  });
}

let renewal: Promise<User | null> | null = null;
let leavingSession = false;

export async function expireSession(): Promise<void> {
  if (leavingSession) return;
  leavingSession = true;
  userManager.stopSilentRenew();
  try {
    await userManager.removeUser();
  } finally {
    // A full navigation also discards all cached data and pending UI actions.
    window.location.replace("/?sessionExpired=1");
  }
}

export function renewUser(rejectedToken?: string): Promise<User | null> {
  if (leavingSession) return Promise.resolve(null);
  if (renewal) return renewal;
  renewal = (async () => {
    try {
      const current = await userManager.getUser();
      // Another request may already have replaced the rejected access token.
      if (rejectedToken && current && !current.expired && current.access_token !== rejectedToken) return current;
      const renewed = await userManager.signinSilent();
      if (renewed?.access_token && !renewed.expired && (!current || renewed.profile.sub === current.profile.sub)) return renewed;
    } catch {
      // The identity provider must authorize renewal; never bypass its decision.
    }
    await expireSession();
    return null;
  })().finally(() => { renewal = null; });
  return renewal;
}

userManager.events.addAccessTokenExpiring(() => { void renewUser(); });
userManager.events.addAccessTokenExpired(() => { void renewUser(); });
userManager.events.addUserSignedOut(() => { void expireSession(); });

export async function logout(): Promise<void> {
  const user = await userManager.getUser();
  try {
    await userManager.signoutRedirect({
      id_token_hint: user?.id_token,
      post_logout_redirect_uri: `${window.location.origin}/`
    });
  } catch {
    await userManager.removeUser();
    window.location.assign("/");
  }
}
